import { Alert, Box, Slide, Snackbar } from '@mui/material';
import type { SlideProps } from '@mui/material/Slide';

import { useToastContext } from './context/ToastContext';

function SlideTransition(props: SlideProps) {
	return <Slide {...props} direction="up" />;
}

/**
 * Stackable toast notifications rendered with MUI Snackbar + Alert.
 * Mounted once via {@link ToastContextProvider}.
 */
export default function Toast(): JSX.Element {
	const { toasts, closeToast } = useToastContext();

	return (
		<Box
			aria-live="polite"
			aria-relevant="additions"
			sx={{
				position: 'fixed',
				bottom: (theme) => theme.spacing(3),
				right: (theme) => theme.spacing(3),
				zIndex: (theme) => theme.zIndex.snackbar,
				display: 'flex',
				flexDirection: 'column-reverse',
				gap: 1,
				alignItems: 'flex-end',
				width: '100%',
				maxWidth: 420,
				pointerEvents: 'none',
			}}
		>
			{toasts.map((toast) => (
				<Snackbar
					key={toast.id}
					open
					autoHideDuration={toast.duration}
					onClose={(_, reason) => {
						if (reason === 'clickaway') return;
						closeToast(toast.id);
					}}
					TransitionComponent={SlideTransition}
					sx={{
						position: 'relative',
						inset: 'unset',
						transform: 'none',
						left: 'unset',
						top: 'unset',
						right: 'unset',
						bottom: 'unset',
						width: '100%',
					}}
				>
					<Alert
						severity={toast.type}
						variant="filled"
						onClose={() => closeToast(toast.id)}
						sx={{
							pointerEvents: 'auto',
							width: '100%',
							boxShadow: 3,
						}}
					>
						{toast.message}
					</Alert>
				</Snackbar>
			))}
		</Box>
	);
}
